// Chat text helpers shared by the support (team) chat and the in-call meeting chat.
//
// Messages are stored as plain text. The Syncfusion chat input hands over the HTML of its
// editable box (`a &amp; b`, `<br>`, `<div>`), and older messages were saved that way, so
// anything shown goes through htmlToPlainText first.

// US date and time, e.g. 09/29/2026 3:05 PM (Syncfusion format string)
export const CHAT_TIMESTAMP_FORMAT = 'MM/dd/yyyy h:mm a';

const BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'TR']);

/** Turn chat-input HTML into plain text, keeping line breaks and link targets. */
export function htmlToPlainText(value) {
    if (value == null) return '';
    const input = String(value);
    if (!/[<&]/.test(input)) return input;
    if (typeof DOMParser === 'undefined') return input;

    const doc = new DOMParser().parseFromString(`<body>${input}</body>`, 'text/html');
    let out = '';
    const walk = (node) => {
        if (node.nodeType === 3) {
            out += node.nodeValue;
            return;
        }
        if (node.nodeType !== 1) return;
        if (node.tagName === 'BR') {
            out += '\n';
            return;
        }
        if (BLOCK_TAGS.has(node.tagName) && out && !out.endsWith('\n')) {
            out += '\n';
        }
        const before = out.length;
        node.childNodes.forEach(walk);
        if (node.tagName === 'A') {
            const href = node.getAttribute('href') || '';
            const text = out.slice(before).trim();
            if (/^https?:\/\//i.test(href) && href !== text && `${href}/` !== `${text}/`) {
                out += ` (${href})`;
            }
        }
    };
    doc.body.childNodes.forEach(walk);
    return out.replace(/ /g, ' ').trim();
}

// http(s) links and bare www. links. Trailing punctuation is trimmed off below.
const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;

function trimTrailingPunctuation(url) {
    let result = url;
    // Drop a closing bracket only if the link has no matching opening one, e.g. "(see www.x.com)"
    while (/[.,;:!?'")\]}]$/.test(result)) {
        const last = result[result.length - 1];
        if (last === ')' && (result.match(/\(/g) || []).length >= (result.match(/\)/g) || []).length) {
            break;
        }
        result = result.slice(0, -1);
    }
    return result;
}

/** Split text into plain and link parts: [{ text }, { text, href }, ...] */
export function splitLinks(value) {
    const text = value == null ? '' : String(value);
    const parts = [];
    let lastIndex = 0;
    URL_PATTERN.lastIndex = 0;
    let match;
    while ((match = URL_PATTERN.exec(text)) !== null) {
        const url = trimTrailingPunctuation(match[0]);
        if (!url) continue;
        if (match.index > lastIndex) {
            parts.push({ text: text.slice(lastIndex, match.index) });
        }
        const href = /^www\./i.test(url) ? `https://${url}` : url;
        parts.push({ text: url, href });
        lastIndex = match.index + url.length;
        URL_PATTERN.lastIndex = lastIndex;
    }
    if (lastIndex < text.length) {
        parts.push({ text: text.slice(lastIndex) });
    }
    return parts;
}

export function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export const NEW_TAB_NOTE = '(opens in a new tab)';

/**
 * Plain text to safe HTML for the Syncfusion chat, which renders message text as HTML:
 * everything escaped, links clickable and opening in a new tab so the chat stays open.
 */
export function linkifyToHtml(value) {
    return splitLinks(value)
        .map((part) => {
            const text = escapeHtml(part.text).replace(/\n/g, '<br>');
            if (!part.href) return text;
            return `<a href="${escapeHtml(part.href)}" target="_blank" rel="noopener noreferrer">${text}<span class="sr-only"> ${NEW_TAB_NOTE}</span></a>`;
        })
        .join('');
}

/** Time only, US style: 3:05 PM */
export function formatUsTime(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** Date and time, US style: 09/29/2026, 3:05 PM */
export function formatUsDateTime(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
}
